import { useCallback, useMemo } from 'react';
import {
  bonusRestrictedGroups,
  promoRestrictedGroups,
  USER_GROUP,
} from '@/helpers/user';
import { useCMSConfig } from '@/modules/common';
import { useGetCommonStore } from '@/store';
import {
  BONANZA_PROMO_ALLOWED_COUNTRIES,
  BONANZA_PROMO_RESTRICTED_GROUPS,
  checkIsCmsPromoAvailable,
  FORTUNE_WHEEL_RESTRICTED_GROUPS,
} from '../helpers';
import { useBonanzaPromo } from './useBonanzaPromo';

export const usePromotionsAvailability = (headlinePromoKey?: string) => {
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();
  const countryCode = useCommonStore.use.countryCode();

  const { cmsConfig } = useCMSConfig();

  const { isPromoCompleted } = useBonanzaPromo();

  const isBonanzaPromoAvailable = useMemo(() => {
    if (!(cmsConfig && cmsConfig.bonanzaPromo?.dateRange)) return;

    const availableByDateAndGroups = checkIsCmsPromoAvailable(
      cmsConfig.bonanzaPromo.availableByDate,
      user,
      BONANZA_PROMO_RESTRICTED_GROUPS,
      countryCode,
      BONANZA_PROMO_ALLOWED_COUNTRIES
    );

    if (isPromoCompleted) return false;

    return availableByDateAndGroups;
  }, [cmsConfig, isPromoCompleted, user, countryCode]);

  const isScratchGameAvailable = useMemo(
    () =>
      checkIsCmsPromoAvailable(cmsConfig?.scratchGame.availableByDate, user),
    [cmsConfig?.scratchGame.availableByDate, user]
  );

  const isFortuneWheelAvailable = useMemo(
    () =>
      checkIsCmsPromoAvailable(
        cmsConfig?.fortuneWheel.availableByDate,
        user,
        FORTUNE_WHEEL_RESTRICTED_GROUPS
      ),
    [cmsConfig?.fortuneWheel.availableByDate, user]
  );

  const isDiceRollAvailable = useMemo(
    () => checkIsCmsPromoAvailable(cmsConfig?.diceRoll.availableByDate, user),
    [cmsConfig?.diceRoll.availableByDate, user]
  );

  const isQuestAvailable = useMemo(
    () => checkIsCmsPromoAvailable(cmsConfig?.quest.availableByDate, user),
    [cmsConfig?.quest.availableByDate, user]
  );

  const isMissionAvailable = useMemo(
    () =>
      checkIsCmsPromoAvailable(
        cmsConfig?.mission.availableByDate,
        user,
        bonusRestrictedGroups
      ),
    [cmsConfig?.mission.availableByDate, user]
  );

  const isHeadlinePromoAvailable = useCallback(
    (promoKey?: string) => {
      if (!promoKey) return false;

      return checkIsCmsPromoAvailable(
        cmsConfig?.headlinePromo(promoKey).availableByDate,
        user,
        [
          USER_GROUP.RISK_SEGMENT_S1,
          USER_GROUP.RISK_SEGMENT_S2,
          ...promoRestrictedGroups,
        ]
      );
    },
    [cmsConfig, user]
  );

  const isAnyPromoAvailable = useMemo(
    () =>
      isBonanzaPromoAvailable ||
      isScratchGameAvailable ||
      isFortuneWheelAvailable ||
      isDiceRollAvailable ||
      isQuestAvailable ||
      isMissionAvailable ||
      isHeadlinePromoAvailable(headlinePromoKey),
    [
      isBonanzaPromoAvailable,
      isScratchGameAvailable,
      isFortuneWheelAvailable,
      isQuestAvailable,
      isDiceRollAvailable,
      isMissionAvailable,
      isHeadlinePromoAvailable,
      headlinePromoKey,
    ]
  );

  return {
    isQuestAvailable,
    isAnyPromoAvailable,
    isBonanzaPromoAvailable,
    isScratchGameAvailable,
    isFortuneWheelAvailable,
    isDiceRollAvailable,
    isMissionAvailable,
    isHeadlinePromoAvailable,
  };
};
